/**
 * Central SEO config: route -> metadata.
 * Base URL from VITE_SITE_URL; fallback for i18n: use en when de missing.
 */

export const SITE_URL =
  (import.meta.env?.VITE_SITE_URL as string | undefined) ||
  process.env.VITE_SITE_URL ||
  'https://www.julianquerido.com';
export const DEPLOY_ENV =
  (import.meta.env?.VITE_DEPLOY_ENV as string | undefined) ||
  process.env.VITE_DEPLOY_ENV ||
  process.env.DEPLOY_ENV ||
  'production';
export const isStaging = DEPLOY_ENV === 'staging';

export interface RouteMeta {
  title: { en: string; de?: string };
  description: { en: string; de?: string };
  /** Path only (no origin). */
  canonicalPath: string;
  /** Path to OG image (e.g. /profile-og.png). */
  ogImage?: string;
  ogType?: 'website' | 'article';
}

const defaultOgImage = `${SITE_URL}/profile-og.png`;

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: { en: 'Julian Querido | Product Engineer · Frontend & AI Products', de: 'Julian Querido | Product Engineer · Frontend & KI-Produkte' },
    description: {
      en: 'Julian Querido is a Product Engineer in St. Gallen, Switzerland, building modern web experiences, frontend systems, and practical AI-supported products.',
      de: 'Julian Querido ist Product Engineer in St. Gallen, Schweiz, und entwickelt moderne Web-Erlebnisse, Frontend-Systeme und praxisnahe KI-gestützte Produkte.',
    },
    canonicalPath: '/',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/about': {
    title: { en: 'About | Julian Querido', de: 'Über mich | Julian Querido' },
    description: {
      en: 'About Julian Querido — Product Engineer with 20+ years of experience across web, mobile, frontend architecture, UX, React, and TypeScript.',
      de: 'Über Julian Querido — Product Engineer mit über 20 Jahren Erfahrung in Web, Mobile, Frontend-Architektur, UX, React und TypeScript.',
    },
    canonicalPath: '/about',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/projects': {
    title: { en: 'Projects | Julian Querido', de: 'Projekte | Julian Querido' },
    description: {
      en: 'Selected projects by Julian Querido across AI-supported products, scientific software, cross-platform apps, data-heavy interfaces, and modern frontend systems.',
      de: 'Ausgewählte Projekte von Julian Querido: KI-gestützte Produkte, wissenschaftliche Software, plattformübergreifende Apps, datenintensive Oberflächen und moderne Frontend-Systeme.',
    },
    canonicalPath: '/projects',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/contact': {
    title: { en: 'Contact | Julian Querido', de: 'Kontakt | Julian Querido' },
    description: {
      en: 'Contact Julian Querido about product engineering, frontend development, AI product ideas, and thoughtful collaborations.',
      de: 'Kontakt mit Julian Querido für Product Engineering, Frontend-Entwicklung, KI-Produktideen und sinnvolle Zusammenarbeit.',
    },
    canonicalPath: '/contact',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/skills': {
    title: { en: 'Skills | Julian Querido', de: 'Skills | Julian Querido' },
    description: {
      en: 'Skills and expertise in product engineering, React, TypeScript, frontend architecture, UX, mobile development, testing, and practical AI workflows.',
      de: 'Fähigkeiten in Product Engineering, React, TypeScript, Frontend-Architektur, UX, Mobile-Entwicklung, Testing und praxisnahen KI-Workflows.',
    },
    canonicalPath: '/skills',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/testimonials': {
    title: { en: 'Testimonials | Julian Querido', de: 'Referenzen | Julian Querido' },
    description: {
      en: 'Testimonials and references for Julian Querido, Product Engineer and frontend specialist.',
      de: 'Referenzen für Julian Querido, Product Engineer und Frontend-Spezialist.',
    },
    canonicalPath: '/testimonials',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/privacy': {
    title: { en: 'Privacy Policy | Julian Querido', de: 'Datenschutz | Julian Querido' },
    description: {
      en: 'Privacy policy for julianquerido.com. How we collect, use, and protect your data.',
      de: 'Datenschutzerklärung für julianquerido.com.',
    },
    canonicalPath: '/privacy',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/cookies': {
    title: { en: 'Cookie Policy | Julian Querido', de: 'Cookie-Richtlinie | Julian Querido' },
    description: {
      en: 'Cookie policy for julianquerido.com. Which cookies we use and why.',
      de: 'Cookie-Richtlinie für julianquerido.com.',
    },
    canonicalPath: '/cookies',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/cookie-settings': {
    title: { en: 'Cookie Settings | Julian Querido', de: 'Cookie-Einstellungen | Julian Querido' },
    description: {
      en: 'Manage your cookie preferences for julianquerido.com.',
      de: 'Cookie-Einstellungen für julianquerido.com verwalten.',
    },
    canonicalPath: '/cookie-settings',
    ogImage: defaultOgImage,
    ogType: 'website',
  },
  '/services/legacy-frontend-rescue': {
    title: { en: 'Legacy Frontend Rescue | Julian Querido', de: 'Legacy Frontend Rescue | Julian Querido' },
    description: {
      en: 'Modernise legacy frontends through practical assessment, incremental migration, maintainable architecture, and user-focused improvements.',
      de: 'Legacy-Frontends modernisieren — mit praktischer Analyse, schrittweiser Migration, wartbarer Architektur und nutzerorientierten Verbesserungen.',
    },
    canonicalPath: '/services/legacy-frontend-rescue',
    ogImage: `${SITE_URL}/og/legacy-rescue.png`,
    ogType: 'website',
  },
};

/** Get metadata for a path; project detail uses dynamic overrides. `lang` reserved for i18n. */
export function getMetaForPath(pathname: string, _lang?: 'en' | 'de'): RouteMeta | undefined {
  void _lang; // reserved for future i18n route keys
  return ROUTE_META[pathname];
}

/** Build full canonical URL. */
export function canonicalUrl(path: string): string {
  const base = SITE_URL.replace(/\/$/, '');
  const pathNorm = path.startsWith('/') ? path : `/${path}`;
  return `${base}${pathNorm}`;
}

/** Choose title/description with i18n fallback (de -> en). */
export function pickMeta(
  meta: { title: { en: string; de?: string }; description: { en: string; de?: string } },
  lang: string
): { title: string; description: string } {
  const isDe = lang === 'de';
  return {
    title: (isDe && meta.title.de) ? meta.title.de : meta.title.en,
    description: (isDe && meta.description.de) ? meta.description.de : meta.description.en,
  };
}

/** Person schema for homepage JSON-LD. */
export function getPersonJsonLd(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}/#person`,
    name: 'Julian Querido',
    jobTitle: 'Product Engineer',
    description: 'Product Engineer in St. Gallen, Switzerland, building modern web experiences, frontend systems, and practical AI-supported products with React, TypeScript, UX, and product thinking.',
    url: SITE_URL,
    address: { '@type': 'PostalAddress', addressRegion: 'St. Gallen', addressCountry: 'CH' },
    sameAs: [
      'https://github.com/julianquerido',
      'https://github.com/jsaq007',
      'https://www.linkedin.com/in/julian-querido/',
    ],
    knowsAbout: [
      'Product Engineering',
      'AI Products',
      'React',
      'TypeScript',
      'Frontend Architecture',
      'UX',
      'Product Thinking',
      'Web Development',
    ],
    knowsLanguage: ['en', 'de'],
  };
}

/** CreativeWork / SoftwareApplication schema for a project. */
export function getProjectJsonLd(project: {
  id: string;
  title: { en: string; de: string };
  description: { en: string; de: string };
  summary: { en: string; de: string };
  year: string;
  stack: string[];
  appType: string;
  imageUrl?: string;
  links?: { github?: string; demo?: string };
}): object {
  const url = `${SITE_URL}/projects/${project.id}`;
  const name = project.title.en;
  const description = project.summary?.en ?? project.description.en;
  const isSoftware =
    ['web', 'mobile', 'cross-platform', 'desktop'].includes(project.appType);
  const base: Record<string, unknown> = {
    '@type': isSoftware ? 'SoftwareApplication' : 'CreativeWork',
    '@id': `${url}#project`,
    name,
    description,
    url,
    dateCreated: project.year,
    keywords: project.stack?.slice(0, 10) ?? [],
    creator: { '@id': `${SITE_URL}/#person` },
  };
  if (project.imageUrl) {
    base.image = project.imageUrl.startsWith('http') ? project.imageUrl : `${SITE_URL}${project.imageUrl}`;
  }
  if (isSoftware) {
    (base as Record<string, unknown>).applicationCategory = 'DeveloperApplication';
    (base as Record<string, unknown>).operatingSystem = project.appType === 'cross-platform' ? 'iOS, Android, Web' : project.appType;
  }
  return { '@context': 'https://schema.org', ...base };
}
